import { Box, Button, Flex, IconButton, List, ListItem, Text, useToast } from "@chakra-ui/react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormTextArea, NumericFormInput } from "components/Forms";
import { MdAdd, MdClose } from "react-icons/md";
import { useQueryClient } from "react-query";
import CategoryForm from "./CategoryForm";
import { organizationQuestionnaireKeys } from "../../../../../../hooks/query";
import { useCreateInvestmentsCategory, useUpdateInvestmentsCategory } from "../../../../../../hooks/query/scope-3";
import * as React from "react";
import { prepareCategoryDefaultValues, showErrorToast, showSuccessToast, translateSubElementString } from "../../../../../../utils";

function CategoryInvestments({
	defaultValues,
	tabKey,
	handleCategoryChange,
	finalize,
	finalizeQuestionnaire,
	isPreview
}) {
	const toast = useToast();
	const queryClient = useQueryClient();
	const {
		id: questionnaireId,
		categories: {
			investments: {
				items, notes, id: categoryId
			} = {
				items: [],
				notes: "",
				id: null
			}
		}
	} = defaultValues;
	const { t } = useTranslation("translation", {
		keyPrefix: "organizationQuestionnaire.scope3",
	});

	const FORM_INPUT_KEY = "investments";

	const investmentsDefaultValues = {
		[FORM_INPUT_KEY]: items ? prepareCategoryDefaultValues(items, ['co2Emission']) : [
			{
				co2Emission: "",
				description: ""
			}
		],
		notes
	};

	const {
		register,
		control,
		handleSubmit,
		formState: { errors, isDirty },
	} = useForm({
		defaultValues: investmentsDefaultValues,
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: FORM_INPUT_KEY,
	});

	const formId = `formStep${tabKey}`;

	const queryConfig = {
		onSuccess: () => {
			showSuccessToast(toast, "Category Investments saved!");
			if (finalize) {
				finalizeQuestionnaire(questionnaireId);
			} else {
				queryClient.invalidateQueries(organizationQuestionnaireKeys.orgQuestionnaireById());
				if (!isLoadingOnCreate && !isLoadingOnUpdate) {
					handleCategoryChange();
				}
			}
		},
		onError: err => showErrorToast(toast, err.message)
	};

	const { mutate: create, isLoading: isLoadingOnCreate } = useCreateInvestmentsCategory(queryConfig);
	const { mutate: update, isLoading: isLoadingOnUpdate } = useUpdateInvestmentsCategory(queryConfig);

	const onSubmit = ((data) => {
		if (!isPreview) {
			if (isDirty) {
				const items = data.investments.map((field) => {
					field.id = typeof id === "string" ? null : field.id;
					return field;
				});
				const requestData = {
					items,
					notes: data.notes
				};
				categoryId
					? update({ questionnaireId, id: categoryId, data: requestData })
					: create({ questionnaireId, data: requestData });
			} else {
				finalize ? finalizeQuestionnaire(questionnaireId) : handleCategoryChange();
			}
		} else {
			handleCategoryChange();
		}
	});

	return (
		<CategoryForm onSubmit={handleSubmit(onSubmit)} id={formId}>
			<Text opacity="0.75" fontSize="sm">
				{t(`categories.${tabKey}Desc`)}
			</Text>
			<List marginBlockStart="4">
				{fields.map((item, index) => (
					<ListItem key={item.id} marginBlockEnd="2">
						<Flex alignItems="center" gap="2">
							<Box
								display="flex"
								justifyContent="center"
								alignItems="center"
								flexShrink="0"
								inlineSize="8"
								blockSize="8"
								borderWidth="thin"
								borderRadius="full"
								fontSize="sm"
								fontWeight="semibold"
								marginBlockStart="6"
							>
								{index + 1}
							</Box>
							<NumericFormInput
								label={translateSubElementString("organizationQuestionnaire.scope3.inputs.tCO2e")}
								errors={errors}
								{...register(`${FORM_INPUT_KEY}.${index}.co2Emission`)}
								disabled={isPreview}
							/>
							{fields.length > 1 && !isPreview && (
								<IconButton
									type="button"
									icon={<MdClose size="24" />}
									flexShrink="0"
									variant="ghost"
									marginBlockStart="5"
									onClick={() => remove(index)}
								/>
							)}
						</Flex>
						<Box marginBlockStart="5">
							<FormTextArea
								label={t(`inputs.${FORM_INPUT_KEY}.description`)}
								errors={errors}
								placeholder={t(`inputs.${FORM_INPUT_KEY}.placeholder`)}
								{...register(`${FORM_INPUT_KEY}.${index}.description`)}
								disabled={isPreview}
							/>
						</Box>
					</ListItem>
				))}
			</List>

			{!isPreview && <Box paddingBlock="4">
				<Button
					variant="ghost"
					display="flex"
					alignItems="center"
					onClick={() => {
						append({
							co2Emission: "",
							description: ""
						});
					}}
					_focus={{
						outline: "none",
						textDecoration: "underline"
					}}
				>
					<IconButton
						as="div"
						variant="outline"
						borderRadius="full"
						size="sm"
						tabIndex={-1}
						pointerEvents="none"
						flexShrink="0"
						icon={<MdAdd size="20" />}
					/>
					<Text marginInlineStart="2">{"Add another item"}</Text>
				</Button>
			</Box>}
			<FormTextArea
				label={t("inputs.notes")}
				errors={errors}
				{...register("notes")}
				disabled={isPreview}
				maxHeight="50"
			/>
		</CategoryForm>
	);
}

export default CategoryInvestments;


